<template>
    <div class="text-sm text-gray-700 mt-2 md:mt-4 mb-4 leading-tight italic">
        Welcome to Performance Leaders which ranks {{ rankText }} by {{ periodLabel }} Percent Change (the percentage difference between the last price and {{ periodAgo }}).
    </div>
    <div class="border-t-2 border-blue-600 mt-2">
        <div class="table-header justify-between">
            <select-c
                v-model="market"
                @change="loadChart"
                :options="markets"
                label="Market:"
                wrapper-class="xs:w-full"
            />
            <!-- <div class="relative xs:w-full xs:mb-1">
                <label class="leading-none self-center text-black mr-1" for="performanceMarket">Market:</label>
                <select class="form-control form-control-sm form-control-fixed bg-white mb-1" v-model="market" @change="loadChart" id="performanceMarket" aria-label="Select Market">
                    <template v-for="option in markets">
                        <option :class="{ 'bg-yellow-200': option.highlight }" :value="option.value">
                            {{ option.label }}
                        </option>
                    </template>
                </select>
            </div> -->
            <select-c
                v-model="field"
                @change="loadChart"
                :options="fields"
                label="Period:"
                wrapper-class="xs:w-full"
            />
            <!-- <div class="relative xs:w-full xs:mb-1">
                <label class="leading-none self-center text-black mr-1" for="performancePeriod">Period:</label>
                <select class="form-control form-control-sm form-control-fixed bg-white mb-1" v-model="field" @change="loadChart" id="performancePeriod" aria-label="Select Period">
                    <template v-for="option in fields">
                        <option :class="{ 'bg-yellow-200': option.highlight }" :value="option.value">
                            {{ option.label }}
                        </option>
                    </template>
                </select>
            </div> -->
        </div>
    </div>
    <div id="performance-chart" class="border border-gray-200" :style="chartContainerStyle"></div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { useLocationHash } from '../mixins/LocationHash';

const props = defineProps({
    futuresRoots: Array,
    forexRoots: Array,
});

const emit = defineEmits();

const { searchHash, addToHash } = useLocationHash();

const chartContainerStyle = ref({});
const chart = ref(null);
const market = ref(searchHash('market')?.value || 'futures');
const field = ref(searchHash('field')?.value || 'percentChange');
const markets = ref([
    { label: 'Futures', value: 'futures', default: market.value === 'futures' },
    { label: 'Forex', value: 'forex', default: market.value === 'forex' },
]);
const fields = ref([
    { label: 'Today', value: 'percentChange', default: field.value === 'percentChange' },
    { label: '5-Day', value: 'percentChange5d', default: field.value === 'percentChange5d' },
    { label: '1-Month', value: 'percentChange1m', default: field.value === 'percentChange1m' },
    { label: '3-Month', value: 'percentChange3m', default: field.value === 'percentChange3m' },
    { label: '6-Month', value: 'percentChange6m', default: field.value === 'percentChange6m' },
    { label: '12-Month', value: 'percentChange1y', default: field.value === 'percentChange1y' },
    { label: 'Year-to-Date', value: 'percentChangeYtd', default: field.value === 'percentChangeYtd' },
]);
const data = ref([]);

const updateChartContainerHeight = () => {
    if (chart.value) {
        const numRows = data.value.length; // Get the number of rows in your data
        const rowHeight = 28; // Adjust this value based on your design
        const calculatedHeight = numRows * rowHeight;

        // Update the style for the chart container
        chartContainerStyle.value = {
            height: `${calculatedHeight}px`,
        };
    }
};

const rankText = computed(() => {
    if (market.value === 'futures') {
        return 'futures contracts';
    } else if (market.value === 'forex') {
        return 'forex crossrates';
    }
});

const periodLabel = computed(() => {
    const selectedField = fields.value.find((mField) => mField.value === field.value);
    if (selectedField) {
        return selectedField.label === 'Today' ? "today's" : selectedField.label;
    }
});

const periodAgo = computed(() => {
    switch (field.value) {
        case 'percentChange':
            return `yesterday's price`;
        case 'percentChange5d':
            return `the price 5 days ago`;
        case 'percentChange1m':
            return `the price 1 month ago`;
        case 'percentChange3m':
            return `the price 3 months ago`;
        case 'percentChange6m':
            return `the price 6 months ago`;
        case 'percentChange1y':
            return `the price 1 year ago`;
        case 'percentChangeYtd':
            return `the year-to-date price`;
        default:
            return '';
    }
});

watch(market, () => {
    addToHash('market', market.value);
});

watch(field, () => {
    addToHash('field', field.value);
});

onMounted(() => {
    // Wait for HighCharts to load
    window.addEventListener('load', loadChart);
});

onBeforeUnmount(() => {
    // Clean up event listeners when the component is unmounted
    removeLabelEvents();
});

const setFontFamily = () => {
    window.Highcharts.setOptions({
        chart: {
            style: {
                fontFamily: 'Roboto',
            },
        },
    });
};

const formatChartData = (response) => {
    return response.map((mRow) => {
        let color;

        if (mRow.raw[field.value] > 0) {
            color = 'green';
        } else if (mRow.raw[field.value] < 0) {
            color = 'red';
        } else {
            color = 'purple';
        }

        return {
            color,
            y: parseFloat(Math.abs(mRow.raw[field.value] * 100).toFixed(2)),
            name: mRow.symbolName,
        };
    });
};

const formatLabels = (response) => {
    return response.map((mRow) => {
        return market.value === 'futures' ? `${mRow.symbolName} ${mRow.contractMonthYear}` : mRow.symbolName;
    });
};

const goToSymbol = (index) => {
    const route = 'interactive-charts.symbol';
    let params;

    if (market.value === 'futures') {
        params = { section: 'futures', symbol: data.value[index].symbol };
    } else if (market.value === 'forex') {
        params = { section: 'forex', symbol: data.value[index].symbol };
    }

    window.location = window.route(route, params).toString();
};

const addLabelEvents = () => {
    document.querySelectorAll('.highcharts-xaxis-labels text').forEach((element, index) => {
        element.addEventListener('click', () => {
            goToSymbol(index);
        });
    });
};

const removeLabelEvents = () => {
    document.querySelectorAll('.highcharts-xaxis-labels text').forEach((element) => {
        element.removeEventListener('click', goToSymbol);
    });
};

const updateChart = (response) => {
    const chartOptions = {
        accessibility: {
            enabled: false,
        },
        series: [
            {
                type: 'column',
                data: formatChartData(response),
                dataLabels: {
                    enabled: true,
                    format: '{point.y}%',
                },
            },
        ],
        plotOptions: {
            series: {
                minPointLength: 3,
            },
        },
        chart: {
            inverted: true,
        },
        credits: {
            enabled: false,
        },
        xAxis: {
            categories: formatLabels(response),
            labels: {
                style: {
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    color: '#0064B6',
                    fontSize: '13px',
                },
            },
        },
        yAxis: {
            labels: {
                enabled: false,
            },
            title: {
                text: undefined,
            },
        },
        legend: {
            enabled: false,
        },
        tooltip: {
            enabled: false,
        },
        title: {
            text: undefined,
        },
    };

    chart.value = window.Highcharts.chart('performance-chart', chartOptions);
};

const loadChart = () => {
    setFontFamily();

    if (chart.value) {
        chart.value.showLoading();
        removeLabelEvents();
    }

    axios
        .post(window.route('api.quotes'), {
            symbols: (market.value === 'futures' ? props.futuresRoots : props.forexRoots).join(),
            fields: [field.value, 'symbolName', 'symbol', 'contractMonthYear'].join(),
            orderBy: field.value,
            orderDir: 'desc',
        })
        .then((response) => {
            updateChart(response.data.data);
            data.value = response.data.data;
            addLabelEvents();
            updateChartContainerHeight(); // Update the initial height
        })
        .catch((error) => {
            // Handle error
        })
        .then(() => {
            if (chart.value) {
                chart.value.hideLoading();
            }
        });
};
</script>

<style lang="scss">
#performance-chart {
    height: 1568px;
}
</style>
